import { useMemo, useState } from 'react';

import { Box } from 'approot/shared/design-system/box/box.style';
import LessonMaterialEditCard from './lesson-materials-item-edit';
import type { GetLessonTeachingAndLearningSequenceQuery } from '../teaching-notes.graphql.generated';
import { LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_STUDENT } from 'global/constants';
import { LinkButton } from 'approot/shared/styles/buttons/buttons';
import customNanoId from 'lib/nanoid/nanoid';
import { grey10, grey7 } from 'approot/shared/styles/colours.style';
import {
  NotificationContent,
  NotificationTip,
  NotificationTipText,
} from 'approot/shared/notifications/notification-tip.style';
import { MaterialItem } from './lesson-materials-item-edit.constants';
import { initialiseMaterials } from './lesson-materials-edit.utils';
import type { Pluck } from 'lib/gql/pluck';

const LessonMaterialsEdit = ({
  note,
  onChange,
}: {
  note: Pluck<
    GetLessonTeachingAndLearningSequenceQuery,
    'getLessonTeachingAndLearningSequence'
  >;
  onChange: (
    note: Pluck<
      GetLessonTeachingAndLearningSequenceQuery,
      'getLessonTeachingAndLearningSequence'
    >
  ) => void;
}) => {
  const [materials, setMaterials] = useState<MaterialItem[]>(
    initialiseMaterials(note)
  );

  const legacyMaterials = useMemo(() => {
    return (
      note?.lessonMaterials?.filter(m => !m.materialId && m.name.length > 0) ||
      []
    );
  }, [note?.lessonMaterials]);

  const isNothingBeingEdited = useMemo(
    () => materials.every(m => !m.isEditMode),
    [materials]
  );

  return (
    <Box $marginBottom={24} data-selector="activity-materials-section-edit">
      {legacyMaterials.length > 0 && (
        <NotificationTip $marginBottom={16}>
          <NotificationContent>
            <NotificationTipText colour={grey10} marginBottom={4}>
              Legacy Materials List For Reference - Please Update to New Format
            </NotificationTipText>
            <NotificationTipText colour={grey7} marginBottom={0}>
              {legacyMaterials.map(m => m.name).join(', ')}
            </NotificationTipText>
          </NotificationContent>
        </NotificationTip>
      )}

      {materials.map((material, index, array) => {
        const isLegacyMaterial = !!legacyMaterials.find(
          m => m.id === material.id
        );

        return (
          <LessonMaterialEditCard
            key={material.id}
            material={material}
            onEdit={item => {
              setMaterials(
                array.map(a => ({ ...a, isEditMode: a.id === item.id }))
              );
            }}
            onSave={updatedMaterial => {
              array[index] = updatedMaterial;
              setMaterials([...array]);

              const { isEditMode, ...rest } = updatedMaterial;
              const updatedLessonMaterials = [...(note.lessonMaterials || [])];
              updatedLessonMaterials[index] = rest;
              onChange({
                ...note,
                lessonMaterials: updatedLessonMaterials,
              });
            }}
            onDeleteItem={
              array.length > 1
                ? () => {
                    array.splice(index, 1);
                    setMaterials([...array]);

                    const updatedLessonMaterials = [
                      ...(note.lessonMaterials || []),
                    ];
                    updatedLessonMaterials.splice(index, 1);
                    onChange({
                      ...note,
                      lessonMaterials: updatedLessonMaterials,
                    });
                  }
                : undefined
            }
            isLegacyMaterial={isLegacyMaterial}
            existingMaterials={materials}
          />
        );
      })}

      {isNothingBeingEdited && (
        <LinkButton
          type="button"
          onClick={() => {
            setMaterials([
              ...materials.map(a => ({ ...a, isEditMode: false })),
              {
                __typename: 'LessonMaterial',
                id: customNanoId(),
                name: '',
                numberOfItems: 1,
                normalisedNumberOfItems: null,
                groupType: LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_STUDENT,
                numberInGroup: null,
                alternativeMaterials: [],
                materialId: null,
                note: null,
                isOptional: false,
                isEditMode: true,
                isReusable: false,
                isStandardised: true,
                unitOfMeasurementId: null,
                unitsOfMeasurement: [],
              },
            ]);
          }}
          data-selector="add-another-material-button"
        >
          Add Another Material
        </LinkButton>
      )}
    </Box>
  );
};

export default LessonMaterialsEdit;
